import { gql, useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect } from 'react';
import { useResetApolloLinks } from '../app/apollo/getApolloClient';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import type {
  CreateVisitorMutation,
  CreateVisitorMutationVariables,
  VisitorSessionQuery,
  VisitorSessionQueryVariables,
} from '../graphql/generated/graphqlTypes';
import { SessionActions } from './sessionSlice';
import visitorAuthTokenUtil from './visitorAuthTokenUtil';

const USE_VISITOR_SESSION_VISITOR_SESSION_FRAGMENT = gql`
  fragment useVisitorSession_visitorSession on VisitorSession {
    authToken
    user {
      id
      name
      title
      avatar {
        original
        thumb
      }
    }
    intercomHash
  }
`;

const VISITOR_SESSION = gql`
  query VisitorSession {
    visitorSession: currentVisitorSession {
      ...useVisitorSession_visitorSession
    }
  }
  ${USE_VISITOR_SESSION_VISITOR_SESSION_FRAGMENT}
`;

const CREATE_VISITOR = gql`
  mutation createVisitor {
    createVisitor {
      ...useVisitorSession_visitorSession
    }
  }
  ${USE_VISITOR_SESSION_VISITOR_SESSION_FRAGMENT}
`;

const useVisitorSession = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.session.user);
  const resetApolloLinks = useResetApolloLinks();

  const { data, loading, refetch } = useQuery<
    VisitorSessionQuery,
    VisitorSessionQueryVariables
  >(VISITOR_SESSION, { skip: !visitorAuthTokenUtil.get() });

  const [createVisitorMutation] = useMutation<
    CreateVisitorMutation,
    CreateVisitorMutationVariables
  >(CREATE_VISITOR);

  const visitorSession = data?.visitorSession;

  useEffect(() => {
    if (visitorSession && !currentUser.id) {
      dispatch(SessionActions.startVisitorSession(visitorSession));
    }
  }, [currentUser.id, dispatch, visitorSession]);

  const createVisitor = useCallback(async () => {
    if (visitorSession) {
      return;
    }
    const createVisitorRes = await createVisitorMutation();
    const session = createVisitorRes.data?.createVisitor;
    if (session) {
      dispatch(SessionActions.startVisitorSession(session));
      await resetApolloLinks();
      await refetch();
    }
  }, [
    createVisitorMutation,
    dispatch,
    refetch,
    resetApolloLinks,
    visitorSession,
  ]);

  return {
    visitorSession,
    ensureVisitorSession: loading ? null : createVisitor,
  };
};

export default useVisitorSession;
